"use client";

import { faSliders } from "@fortawesome/pro-solid-svg-icons";
import { useTranslations } from "next-intl";
import { useId, useState, useSyncExternalStore } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/Icon";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useRouter } from "@/i18n/navigation";
import { moneyStep } from "@/lib/currency-format";
import { toWesternDigits } from "@/lib/phone";
import { useStorefrontRoot } from "./use-storefront-root";

const SORTS = [
	["newest", "sortNewest"],
	["price_asc", "sortPriceLow"],
	["price_desc", "sortPriceHigh"],
] as const;

interface Draft {
	sort: string;
	min: string;
	max: string;
	inStock: boolean;
}

/**
 * Every control in the sheet takes the store's `buttonShape` (`--radius-btn`),
 * the radius its Reset and Show results buttons already wear through `.btn`,
 * so a pill store gets pill rows and inputs rather than boxes beside pills.
 */
const CONTROL = "rounded-[var(--radius-btn,var(--radius-md))]";

/**
 * Price, availability and sort, behind one Filter button: a bottom sheet below
 * `md`, a dialog above it. No category control of any kind: `CategoryRail` is
 * the page's only category surface.
 *
 * The controls edit a draft and nothing navigates until "Show results", so a
 * shopper setting three things pays for one page load, not three.
 */
export function CatalogControls({
	sort,
	minPrice,
	maxPrice,
	inStock,
	decimals,
	priceReversed,
}: {
	sort: string;
	minPrice: string;
	maxPrice: string;
	inStock: boolean;
	/** Currency decimals, so the price step matches the money shape (TRY-484). */
	decimals: number;
	/**
	 * The server saw a reversed range in the URL (a shared or paginated link, not
	 * a click) and dropped both bounds; the inputs still show them, so the message
	 * must too.
	 */
	priceReversed: boolean;
}) {
	const t = useTranslations("shop");
	const router = useRouter();
	const root = useStorefrontRoot();
	const narrow = useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<Draft>({ sort, min: minPrice, max: maxPrice, inStock });
	const [priceError, setPriceError] = useState(false);

	// A reversed range was dropped by the server, so it is not a filter in effect.
	const pricing = !priceReversed && Boolean(minPrice || maxPrice);
	const active = (pricing ? 1 : 0) + (inStock ? 1 : 0) + (sort !== "newest" ? 1 : 0);

	function show() {
		// The draft restarts from the URL each time, so a dismissed sheet
		// forgets what was typed into it rather than resurfacing it later.
		setDraft({ sort, min: minPrice, max: maxPrice, inStock });
		setPriceError(priceReversed);
		setOpen(true);
	}

	function apply() {
		// `type="number"` blanks anything the browser cannot parse, so
		// Arabic-Indic digits never survive the keystroke; folding here covers
		// the value arriving from a URL round-trip. CLAUDE.md §6.
		const min = toWesternDigits(draft.min);
		const max = toWesternDigits(draft.max);
		if (min && max && Number(max) < Number(min)) {
			setPriceError(true);
			return;
		}
		// Read at click time rather than subscribing via `useSearchParams()`:
		// nothing here renders from the params.
		const next = new URLSearchParams(window.location.search);
		const patch: Record<string, string | null> = {
			sort: draft.sort === "newest" ? null : draft.sort,
			min: min || null,
			max: max || null,
			stock: draft.inStock ? "1" : null,
		};
		for (const [k, v] of Object.entries(patch)) {
			if (v) next.set(k, v);
			else next.delete(k);
		}
		next.delete("page");
		setPriceError(false);
		setOpen(false);
		router.push(`/?${next.toString()}`);
	}

	const body = (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				apply();
			}}
			className="flex min-h-0 flex-col"
		>
			<div className="flex min-h-0 flex-col gap-6 overflow-y-auto px-5 pb-2">
				<Section title={t("sort")}>
					<div className="flex flex-col gap-2">
						{SORTS.map(([value, key]) => (
							<label
								key={value}
								className={`${CONTROL} flex cursor-pointer items-center justify-between gap-3 border border-border px-3 py-2.5 text-sm transition has-checked:border-primary has-checked:bg-primary/5 has-focus-visible:ring-2 has-focus-visible:ring-primary/40`}
							>
								{t(key)}
								<input
									type="radio"
									name="sort"
									value={value}
									checked={draft.sort === value}
									onChange={() => setDraft((d) => ({ ...d, sort: value }))}
									className="size-4 accent-primary"
								/>
							</label>
						))}
					</div>
				</Section>

				<Section title={t("price")}>
					<div className="flex items-center gap-2">
						<PriceInput
							label={t("priceMin")}
							value={draft.min}
							step={moneyStep(decimals)}
							invalid={priceError}
							onChange={(v) => setDraft((d) => ({ ...d, min: v }))}
						/>
						<span className="mt-5 text-muted-foreground">–</span>
						<PriceInput
							label={t("priceMax")}
							value={draft.max}
							step={moneyStep(decimals)}
							invalid={priceError}
							onChange={(v) => setDraft((d) => ({ ...d, max: v }))}
						/>
					</div>
					{priceError && <p className="mt-2 text-sm text-danger">{t("priceOrder")}</p>}
				</Section>

				<Section title={t("availability")}>
					<label className="flex cursor-pointer items-center justify-between gap-3 text-sm">
						{t("inStock")}
						<input
							type="checkbox"
							role="switch"
							checked={draft.inStock}
							onChange={(e) => setDraft((d) => ({ ...d, inStock: e.target.checked }))}
							className="peer sr-only"
						/>
						<span
							aria-hidden
							className={`${CONTROL} relative h-6 w-10 shrink-0 bg-border transition peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 after:absolute after:top-0.5 after:start-0.5 after:size-5 after:rounded-[calc(var(--radius-btn,var(--radius-md))_-_2px)] after:bg-white after:shadow after:transition peer-checked:after:translate-x-4 rtl:peer-checked:after:-translate-x-4`}
						/>
					</label>
				</Section>
			</div>

			<div className="flex gap-2 border-t border-border px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
				<button
					type="button"
					onClick={() => {
						setDraft({ sort: "newest", min: "", max: "", inStock: false });
						setPriceError(false);
					}}
					className="btn btn-outline"
				>
					{t("resetFilters")}
				</button>
				<button type="submit" className="btn btn-primary flex-1">
					{t("showResults")}
				</button>
			</div>
		</form>
	);

	const title = <span className="font-sans text-lg font-bold text-text">{t("filters")}</span>;

	return (
		<>
			<button
				type="button"
				onClick={show}
				aria-haspopup="dialog"
				aria-label={active > 0 ? t("filtersActive", { count: active }) : undefined}
				className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-text transition hover:bg-surface-2"
			>
				<Icon icon={faSliders} size={14} />
				{t("filter")}
				{active > 0 && (
					<span className="inline-flex size-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
						{active}
					</span>
				)}
			</button>

			{narrow ? (
				<Sheet open={open} onOpenChange={setOpen}>
					<SheetContent
						side="bottom"
						container={root}
						className="max-h-[85dvh] gap-0 rounded-t-[min(calc(var(--radius)*1.8),1.75rem)] border-border bg-surface text-text"
					>
						<div aria-hidden className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-border" />
						<SheetTitle className="px-5 pt-3 pb-4">{title}</SheetTitle>
						{body}
					</SheetContent>
				</Sheet>
			) : (
				<Dialog open={open} onOpenChange={setOpen}>
					<DialogContent
						container={root}
						className="flex max-w-md flex-col gap-0 rounded-[min(calc(var(--radius)*1.8),1.75rem)] border border-border bg-surface p-0 pt-5 text-base text-text shadow-xl sm:max-w-md"
					>
						<DialogTitle className="px-5 pb-4">{title}</DialogTitle>
						{body}
					</DialogContent>
				</Dialog>
			)}
		</>
	);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<fieldset className="min-w-0">
			<legend className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
				{title}
			</legend>
			{children}
		</fieldset>
	);
}

function PriceInput({
	label,
	value,
	step,
	invalid,
	onChange,
}: {
	label: string;
	value: string;
	step: number;
	invalid: boolean;
	onChange: (value: string) => void;
}) {
	const id = useId();
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-1.5">
			<label htmlFor={id} className="text-xs text-muted-foreground">
				{label}
			</label>
			<input
				id={id}
				type="number"
				inputMode="decimal"
				min={0}
				step={step}
				value={value}
				aria-invalid={invalid || undefined}
				onChange={(e) => onChange(e.target.value)}
				className={`${CONTROL} w-full border border-border bg-surface px-3.5 py-2 text-sm outline-none focus:border-primary aria-invalid:border-danger`}
			/>
		</div>
	);
}

/** Below Tailwind's `md`, where the filters open as a bottom sheet. */
const NARROW = "(width < 48rem)";

function subscribeNarrow(onChange: () => void) {
	const mq = window.matchMedia(NARROW);
	mq.addEventListener("change", onChange);
	return () => mq.removeEventListener("change", onChange);
}

function isNarrow() {
	return window.matchMedia(NARROW).matches;
}
